/**
 * What a program version holds, read the way a buyer reads it: one hairline table with a row per rule and a column
 * per phase (a percentage also shows its dollar value at the account size), then the profit split. Shared by the
 * public programs and rules pages and the buy page (ADR-0029). ADR-0034: the scaling plan; on the full rules page also
 * what a capped payout leaves on the account, the reset with the 7th payout and the program's funding limit.
 */
import type { Phase, ProgramRules } from "@prop/contracts";
import { cn } from "@prop/ui";
import { useRiskT, useTradeT } from "../../i18n";
import { fmtQty, fmtUsd } from "../../lib/fmt";
import { dayBar } from "../../lib/rules";

type Amount = { pct: string } | { usd: string } | undefined;

/** Account size in dollars without cents: $100,000. */
export const sizeUsd = (rules: ProgramRules) => `$${fmtQty(rules.accountSize, 0)}`;

/** A rule amount in dollars, with the percentage of the account under it when it is one. */
function Money({ a, rules }: { a: Amount; rules: ProgramRules }) {
  if (!a) return <>—</>;
  if ("usd" in a) return <>${fmtQty(a.usd, 2)}</>;
  // a display value only: the share of the account size, never stored or sent back
  const v = (Number(rules.accountSize) * Number(a.pct)) / 100;
  return (
    <>
      ${fmtQty(v, 0)}
      <span className="block text-xs font-normal text-muted-foreground">{a.pct} %</span>
    </>
  );
}

/** One label / value hairline row. */
export function Row({ k, v, note }: { k: string; v: string; note?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border py-2.5 last:border-0">
      <dt className="text-sm text-muted-foreground">{k}</dt>
      <dd className="text-right text-sm font-medium tabular-nums">
        {v}
        {note && <span className="block text-xs font-normal text-muted-foreground">{note}</span>}
      </dd>
    </div>
  );
}

/** `fullOnly`: a row of the full rules page only (the summary on the buy page keeps its seven). */
type Rule = { key: string; label: string; note?: string; has: (p: Phase) => boolean; cell: (p: Phase) => React.ReactNode; fullOnly?: boolean };

/** A cell with a quiet line under it. */
const Cell = ({ v, note }: { v: React.ReactNode; note?: string }) => (
  <>
    {v}
    {note && <span className="block text-xs font-normal text-muted-foreground">{note}</span>}
  </>
);

/** What a payout waits for, one line each; micro-scalping only on the full rules page (owner's call pending, ADR-0032). */
export function payoutLines(rules: ProgramRules, full: boolean, t: ReturnType<typeof useTradeT>): string[] {
  const p = rules.payouts;
  if (!p) return [];
  const pd = p.profitableDays;
  const scalp = full ? rules.phases.find((ph) => ph.kind === "funded")?.microScalp : undefined;
  return [
    pd && t(pd.consecutive ? "site.streakCell" : "site.daysCell", { n: pd.days, v: dayBar(pd) }),
    p.consistencyPct && t("site.bestDayCell", { v: p.consistencyPct }),
    Number(p.minPayout) > 0 && t("site.minProfitCell", { v: fmtUsd(p.minPayout) }),
    p.cycle !== "on_demand" && t(`site.cycle.${p.cycle}`),
    scalp && `${t("site.microScalp")}: ${t("site.microScalpNote", { sec: scalp.maxHoldSec })} ${t("site.microScalpCell", { v: scalp.maxProfitSharePct })} (${t("site.fundedOnly")})`,
    full && p.remainder === "segment" && t("site.segmentCell"),
    full && p.remainder === "carry" && t("site.carryCell"),
    full && p.resetAfterPayout && t("site.resetCell", { n: p.resetAfterPayout }),
  ].filter((x): x is string => typeof x === "string" && x.length > 0);
}

/** `full`: the program's rules page — also the lines a summary leaves out (micro-scalping in the funded phase). */
export function ProgramFacts({ rules, className, full = false }: { rules: ProgramRules; className?: string; full?: boolean }) {
  const t = useTradeT();
  const r = useRiskT();
  const phases = rules.phases;
  const first = phases[0];
  const zone = "session" in rules.dayBoundary ? "America/Chicago" : rules.dayBoundary.timezone;
  const city = (zone.split("/").at(-1) ?? zone).replace(/_/g, " ");
  const all: Rule[] = [
    { key: "target", label: t("site.target"), has: (p) => !!p.profitTarget, cell: (p) => <Money a={p.profitTarget} rules={rules} /> },
    {
      key: "dll",
      label: t("site.dll"),
      note: first?.dailyLoss ? r(`programs.bases.${first.dailyLoss.base}`) : undefined,
      has: (p) => !!p.dailyLoss,
      cell: (p) => <Money a={p.dailyLoss?.amount} rules={rules} />,
    },
    { key: "ml", label: t("site.ml"), note: first ? r(`programs.modes.${first.maxLoss.mode}`) : undefined, has: () => true, cell: (p) => <Money a={p.maxLoss.amount} rules={rules} /> },
    {
      key: "days",
      label: t("site.days"),
      has: (p) => !!p.minTradingDays,
      cell: (p) => <Cell v={p.minTradingDays?.days ?? "—"} note={p.minTradingDays?.minDailyProfitPct ? t("site.daysBar", { v: p.minTradingDays.minDailyProfitPct }) : undefined} />,
    },
    { key: "cons", label: t("site.consistency"), has: (p) => !!p.consistency, cell: (p) => (p.consistency ? `≤ ${p.consistency.maxPct} %` : "—") },
    // The rest of what can end or hold an account (release audit, ADR-0042): the full rules page lists every rule.
    // On the floating loss too (ADR-0043): an open trade breaches as soon as it passes the cap.
    {
      key: "tradeLoss",
      label: t("site.tradeLoss"),
      fullOnly: true,
      has: (p) => !!p.maxTradeLoss,
      cell: (p) => <Cell v={<Money a={p.maxTradeLoss} rules={rules} />} note={p.maxTradeLoss ? t("site.tradeLossNote") : undefined} />,
    },
    {
      key: "time",
      label: t("site.time"),
      fullOnly: true,
      has: (p) => !!(p.timeWindow || p.maxDurationDays),
      cell: (p) => (p.timeWindow ? t("site.windowCell", { h: fmtQty(p.timeWindow.minutes / 60, 2) }) : p.maxDurationDays ? t("site.daysN", { n: p.maxDurationDays }) : "—"),
    },
    { key: "maxDays", label: t("site.maxDays"), fullOnly: true, has: (p) => !!p.maxTradingDays, cell: (p) => p.maxTradingDays ?? "—" },
    { key: "idle", label: t("site.inactivity"), fullOnly: true, has: (p) => !!p.inactivityDays, cell: (p) => (p.inactivityDays ? t("site.inactivityCell", { n: p.inactivityDays }) : "—") },
    {
      key: "contracts",
      label: t("site.contracts"),
      fullOnly: true,
      has: (p) => !!p.positionLimits?.maxContracts,
      cell: (p) => (p.positionLimits?.maxContracts ? t("site.contractsCell", { n: p.positionLimits.maxContracts }) : "—"),
    },
    {
      key: "flat",
      label: t("site.flat"),
      fullOnly: true,
      has: (p) => !!p.timeRules?.flatByLocalTime,
      cell: (p) => <Cell v={p.timeRules?.flatByLocalTime ?? "—"} note={t("site.flatNote", { city })} />,
    },
    // ADR-0043: FX / CFD funded phases close for the weekend and keep a news window.
    {
      key: "weekend",
      label: t("site.weekend"),
      fullOnly: true,
      has: (p) => p.timeRules?.weekend === "auto_close",
      cell: (p) => (p.timeRules?.weekend === "auto_close" ? <Cell v={t("site.weekendCell")} note={t("site.weekendNote")} /> : "—"),
    },
    {
      key: "news",
      label: t("site.news"),
      fullOnly: true,
      has: (p) => !!p.timeRules?.newsWindowMin,
      cell: (p) => (p.timeRules?.newsWindowMin ? <Cell v={t("site.newsCell", { n: p.timeRules.newsWindowMin })} note={t("site.newsNote")} /> : "—"),
    },
  ];
  const before = payoutLines(rules, full, t);
  const whole = (v: string) => `$${fmtQty(v, 0)}`;
  const program: Array<[string, string]> = [
    ...(rules.scaling ? [[t("site.scaling"), t("site.scalingCell", { step: rules.scaling.stepPct, months: rules.scaling.everyMonths, max: whole(rules.scaling.maxSizeUsd) })] as [string, string]] : []),
    ...(full && rules.fundingLimitUsd ? [[t("site.fundingLimit"), t("site.fundingLimitCell", { v: whole(rules.fundingLimitUsd) })] as [string, string]] : []),
  ];
  const rows = all.filter((x) => (full || !x.fullOnly) && phases.some(x.has));
  const many = phases.length > 1;
  return (
    <table className={cn("w-full table-fixed text-sm", className)}>
      {many && (
        <thead>
          <tr className="border-b border-border">
            <th className="w-[38%] py-2 pr-2 text-left font-normal max-sm:w-[34%]">
              <span className="sr-only">{t("site.family")}</span>
            </th>
            {phases.map((p, i) => (
              <th key={`${i}-${p.name}`} className="py-2 pl-2 text-right font-semibold">
                {p.name}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {rows.map((x) => (
          <tr key={x.key} className="border-b border-border align-top">
            <th scope="row" className={cn("py-2.5 pr-2 text-left font-normal text-muted-foreground", !many && "w-1/2")}>
              {x.label}
              {x.note && <span className="block text-xs">{x.note}</span>}
            </th>
            {phases.map((p, i) => (
              <td key={`${i}-${p.name}`} className="py-2.5 pl-2 text-right font-medium tabular-nums">
                {x.has(p) ? x.cell(p) : "—"}
              </td>
            ))}
          </tr>
        ))}
        {rules.payouts && (
          <tr>
            <th scope="row" className="py-2.5 pr-2 text-left font-normal text-muted-foreground">
              {t("site.split")}
            </th>
            <td colSpan={phases.length} className="py-2.5 pl-2 text-right font-medium tabular-nums">
              {rules.payouts.profitSplitPct} %
            </td>
          </tr>
        )}
        {program.map(([k, v]) => (
          <tr key={k} className="border-t border-border">
            <th scope="row" className="py-2.5 pr-2 text-left font-normal text-muted-foreground">
              {k}
            </th>
            <td colSpan={phases.length} className="py-2.5 pl-2 text-right font-medium tabular-nums">
              {v}
            </td>
          </tr>
        ))}
        {full && (
          // A paragraph, not a figure: on a phone it takes the full width under its label.
          <tr className="border-t border-border align-top max-sm:block">
            <th scope="row" className="py-2.5 pr-2 text-left font-normal text-muted-foreground max-sm:block max-sm:pb-1">
              {t("site.fairPlay")}
            </th>
            <td colSpan={phases.length} className="py-2.5 pl-2 text-left text-[13px] leading-snug max-sm:block max-sm:pl-0 max-sm:pt-0">
              {t("site.fairPlayCell")}
            </td>
          </tr>
        )}
        {before.length > 0 && (
          <tr className="border-t border-border align-top">
            <th scope="row" className="py-2.5 pr-2 text-left font-normal text-muted-foreground">
              {t("site.streak")}
            </th>
            <td colSpan={phases.length} className="py-2.5 pl-2 text-right font-medium tabular-nums">
              {before.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
